import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChannelContext } from '../../context';
import { StoryCardTeaserImageStyle, useStoriesByChannelIdQuery } from '../../services';
import {
  Button,
  ButtonType,
  ConfirmationModal,
  getCssVar,
  getOrganisationDomain,
  IconLabel,
  Modal,
  Radio,
  SearchField,
  Select,
  useDebounce,
  useIsNarrowViewport,
  useSupabase,
} from '../../shared';
import { IntegrationForm } from './IntegrationForm';
import { IntegrationsTable, StatusFilter } from './IntegrationsTable';

import classNames from 'classnames';
import classes from './Integrations.module.scss';

export enum IntegrationType {
  rss = 'rss',
  scraping = 'scraping',
  x = 'x',
  bsky = 'bsky',
  tiktok = 'tiktok',
  youtube = 'youtube',
  facebook = 'facebook',
  instagram = 'instagram',
  linkedin_profile = 'linkedin_profile',
  linkedin = 'linkedin',
  google_news = 'google_news',
  apple = 'apple',
  steady = 'steady',
}

export enum IntegrationStatus {
  active = 'active',
  paused = 'paused',
  error = 'error',
}

export enum IntegrationInputType {
  feed = 'feed',
  parsed = 'parsed',
}

enum ActiveStep {
  type_selection = 'type_selection',
  form = 'form',
}

export interface Integration {
  id: string;
  name: string;
  author_id: number;
  org_id: number;
  channel_id: number;
  mix_id: number;
  original_url: string;
  auto_publish: boolean;
  card_style_type: StoryCardTeaserImageStyle;
  status: IntegrationStatus;
  type: IntegrationType;
  xml_url: string;
  input_type: IntegrationInputType;
  extract_link?: string;
  rename_source_enabled?: boolean;
  rename_source?: string;
  last_run_at: string;
  last_successful_run_at: string;
  last_error: string;
  last_error_at: string;
  cards_created_count: number;
  created_at: string;
  updated_at: string;
  fetch_last_cards: number;
  include_retweets?: boolean;
  include_replies?: boolean;
}

interface SteadyFeedRow {
  id: number;
  name: string | null;
  steady_rss_url: string;
  tchop_channel_id: number;
  default_mix_id: number;
  status: IntegrationStatus;
  channel_name: string | null;
  created_at?: string;
}

// Steady feeds only populate the columns shared with `integrations`; the rest
// are intentionally left undefined and render as blank cells in the table.
const mapSteadyFeedRowToIntegration = ({
  id,
  name,
  steady_rss_url,
  tchop_channel_id,
  default_mix_id,
  status,
  created_at,
}: SteadyFeedRow): Integration =>
  ({
    type: IntegrationType.steady,
    id: String(id),
    name,
    status,
    created_at,
    original_url: steady_rss_url,
    channel_id: tchop_channel_id,
    mix_id: default_mix_id,
  } as Integration);

export const STEADY_FEEDS_TABLE = 'steady_feeds (Live)';

export const INTEGRATIONS_TABLE = 'integrations';

const tableByType = (type: IntegrationType) =>
  type === IntegrationType.steady ? STEADY_FEEDS_TABLE : INTEGRATIONS_TABLE;

export const Integrations: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'integrations' });

  const { supabase, loading: supabaseClientLoading } = useSupabase();

  const isNarrowViewport = useIsNarrowViewport();

  const { id: channelId } = useContext(ChannelContext).channel;

  const isSteady = useMemo(() => getOrganisationDomain() === 'steady', []);

  const { data: stories = [], isFetching: isFetchingStories } = useStoriesByChannelIdQuery({
    channelId,
    filter: { onlyPostIn: true },
  });

  const [searchParams] = useSearchParams();

  const [integrations, setIntegrations] = useState<Integration[]>([]);

  const [integrationsLoading, setIntegrationsLoading] = useState<boolean>(false);

  const [integrationToUpdate, setIntegrationToUpdate] = useState<Integration | null>(null);

  const [integrationToRemove, setIntegrationToRemove] = useState<Integration | null>(null);

  const [selectedIntegrationTypes, setSelectedIntegrationTypes] = useState<IntegrationType[]>([]);

  const presetStoryId = Number(searchParams.get('storyId')) || undefined;

  const isSetupAction = searchParams.get('action') === 'setup';

  const [selectedStoryIds, setSelectedStoryIds] = useState<number[]>(
    presetStoryId && !isSetupAction ? [presetStoryId] : []
  );

  const [activeStep, setActiveStep] = useState<ActiveStep | null>(
    isSetupAction ? ActiveStep.type_selection : null
  );

  const [newIntegrationType, setNewIntegrationType] = useState<IntegrationType | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(StatusFilter.all);

  const [query, setQuery] = useState<string>('');

  const debouncedQuery = useDebounce<string>(query);

  const integrationTypes = useMemo(
    () =>
      (Object.keys(IntegrationType) as IntegrationType[]).filter(
        (type) => type !== IntegrationType.steady || isSteady
      ),
    [isSteady]
  );

  const integrationTypeItems = useMemo(
    () => integrationTypes.map((type) => ({ id: type, title: ctxT(type) })),
    [ctxT, integrationTypes]
  );

  const getIntegrations = useCallback(async () => {
    if (!supabase) {
      return;
    }

    setIntegrationsLoading(true);

    const { data, error } = await supabase
      .from(INTEGRATIONS_TABLE)
      .select(
        `id,
         name,
         type,
         original_url,
         status,
         auto_publish,
         card_style_type,
         input_type,
         extract_link,
         rename_source_enabled,
         rename_source,
         created_at,
         author_id,
         mix_id,
         fetch_last_cards,
         include_retweets,
         include_replies,
         last_run_at,
         last_successful_run_at,
         last_error,
         last_error_at,
         cards_created_count`
      )
      .eq('channel_id', channelId)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error(t('common.error-message'));
      setIntegrationsLoading(false);
      return;
    }

    let integrations = data as Integration[];

    if (isSteady) {
      const { data: steadyData, error: steadyError } = await supabase
        .from(STEADY_FEEDS_TABLE)
        .select(
          `id, 
          name, 
          steady_rss_url, 
          tchop_channel_id, 
          default_mix_id, 
          status, 
          channel_name, 
          created_at`
        )
        .eq('tchop_channel_id', channelId)
        .order('created_at', { ascending: false });

      if (steadyError) {
        toast.error(t('common.error-message'));
        setIntegrationsLoading(false);
        return;
      }

      integrations = [...integrations, ...steadyData.map(mapSteadyFeedRowToIntegration)];
    }

    setIntegrations(integrations);
    setIntegrationsLoading(false);
  }, [channelId, isSteady, supabase, t]);

  useEffect(() => {
    getIntegrations();
  }, [getIntegrations]);

  const toggleStatusHandler = useCallback(
    async ({ id, status, type }: Integration) => {
      if (!supabase) {
        return;
      }

      const { error } = await supabase
        .from(tableByType(type))
        .update({
          status:
            status === IntegrationStatus.active
              ? IntegrationStatus.paused
              : IntegrationStatus.active,
        })
        .eq('id', id);

      if (error) {
        toast.error(t('common.error-message'));
        return;
      }

      await getIntegrations();

      toast.success(ctxT('toggle-status-success'));
    },
    [ctxT, getIntegrations, supabase, t]
  );

  const removeHandler = useCallback(async () => {
    if (!supabase || !integrationToRemove) {
      return;
    }

    const { id, type } = integrationToRemove;

    const { error } = await supabase.from(tableByType(type)).delete().eq('id', id);

    setIntegrationToRemove(null);

    if (error) {
      toast.error(t('common.error-message'));
      return;
    }

    await getIntegrations();

    toast.success(ctxT('remove-success'));
  }, [ctxT, getIntegrations, integrationToRemove, supabase, t]);

  const onClose = useCallback(() => {
    setActiveStep(null);
    setNewIntegrationType(null);
    setIntegrationToUpdate(null);
  }, []);

  const updateHandler = useCallback((integration: Integration) => {
    setActiveStep(ActiveStep.form);
    setIntegrationToUpdate(integration);
  }, []);

  const selectIntegrationType = useCallback((type: IntegrationType) => {
    setNewIntegrationType(type);
    setActiveStep(ActiveStep.form);
  }, []);

  const integrationType = integrationToUpdate?.type ?? newIntegrationType;

  const renderModalBody = useMemo(() => {
    if (activeStep === ActiveStep.type_selection) {
      return (
        <div className={classes['integrations__types-list']}>
          {integrationTypes.map((type) => (
            <div
              key={type}
              className={classes['integrations__types-list-item']}
              onClick={() => selectIntegrationType(type)}
            >
              <div className={classes['integrations__types-list-item-icon']}>
                <IconLabel iconId={type} color={'#6b6d7b'} singleColor />
              </div>
              <div className={classes['integrations__types-list-item-text']}>
                <div className={classes['integrations__types-list-item-text-name']}>
                  {ctxT(type)}
                </div>
                <div className={classes['integrations__types-list-item-text-description']}>
                  {ctxT(`${type}-description`)}
                </div>
              </div>
              <IconLabel
                iconId={'arrow-right'}
                iconSize={20}
                className={classes['integrations__types-list-item-arrow']}
                color={getCssVar('--base-link-text-color')}
                singleColor
              />
            </div>
          ))}
        </div>
      );
    }

    if (activeStep === ActiveStep.form) {
      if (!integrationType) {
        return null;
      }

      return (
        <IntegrationForm
          type={integrationType}
          stories={stories}
          onClose={onClose}
          onCallback={getIntegrations}
          integration={integrationToUpdate}
          presetStoryId={presetStoryId}
          {...(!integrationToUpdate && { onBack: () => setActiveStep(ActiveStep.type_selection) })}
        />
      );
    }

    return null;
  }, [
    activeStep,
    ctxT,
    getIntegrations,
    integrationToUpdate,
    integrationType,
    integrationTypes,
    onClose,
    presetStoryId,
    selectIntegrationType,
    stories,
  ]);

  const modalTitle =
    activeStep === ActiveStep.form && integrationType
      ? [
          t(`common.${integrationToUpdate ? 'edit' : 'new'}`),
          ctxT(integrationType),
          t('common.integration'),
        ].join(' ')
      : ctxT('add-integration');

  const tableContent = useMemo(() => {
    if (integrationsLoading || supabaseClientLoading || isFetchingStories) {
      return (
        <div className={classes['loader']}>
          <Skeleton height={'3rem'} />
          <Skeleton height={'3rem'} />
          <Skeleton height={'3rem'} />
        </div>
      );
    }
    return (
      <IntegrationsTable
        integrations={integrations}
        stories={stories}
        query={debouncedQuery}
        selectedIntegrationTypes={selectedIntegrationTypes}
        selectedStoryIds={selectedStoryIds}
        statusFilter={statusFilter}
        onToggleStatus={toggleStatusHandler}
        onUpdate={updateHandler}
        onRemove={setIntegrationToRemove}
      />
    );
  }, [
    debouncedQuery,
    integrations,
    integrationsLoading,
    isFetchingStories,
    selectedIntegrationTypes,
    selectedStoryIds,
    statusFilter,
    stories,
    supabaseClientLoading,
    toggleStatusHandler,
    updateHandler,
  ]);

  return (
    <div
      className={classNames(classes['integrations'], {
        [classes['integrations--narrow']]: isNarrowViewport,
      })}
    >
      <div className={classes['integrations__header']}>
        <h1 className={classes['integrations__title']}>{ctxT('title')}</h1>
        <Button
          type={ButtonType.primary}
          label={ctxT('add-integration')}
          onClick={() => setActiveStep(ActiveStep.type_selection)}
        />
      </div>

      <div className={classes['integrations__intro']}>{ctxT('intro')}</div>

      <div className={classes['integrations__filters']}>
        <div className={classes['integrations__filters-inputs']}>
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={t('common.type-to-search')}
            className={classes['integrations__filters-search']}
          />

          <div className={classes['integrations__filters-status']}>
            {Object.values(StatusFilter).map((value) => (
              <Radio
                key={value}
                checked={value === statusFilter}
                name={'integration-status'}
                label={ctxT(value)}
                onChange={() => setStatusFilter(value)}
              />
            ))}
          </div>
        </div>

        <Select
          className={classes['integrations__filters-select']}
          label={t('common.type')}
          items={integrationTypeItems}
          selectedItemIds={selectedIntegrationTypes}
          onMultiCheckChange={(ids) => setSelectedIntegrationTypes(ids as IntegrationType[])}
        />

        <Select
          className={classes['integrations__filters-select']}
          label={t('common.stories')}
          items={stories}
          selectedItemIds={selectedStoryIds}
          onMultiCheckChange={setSelectedStoryIds}
        />
      </div>

      {tableContent}

      <Modal
        isOpen={Boolean(activeStep)}
        body={renderModalBody}
        onClose={onClose}
        ariaLabel={modalTitle}
        disableCloseByOverlay
        alignTop
      />

      <ConfirmationModal
        isOpen={Boolean(integrationToRemove)}
        onClose={() => setIntegrationToRemove(null)}
        acceptLabel={t('common.delete')}
        onAccept={removeHandler}
        title={ctxT('delete-confirmation-title')}
        subTitle={ctxT('delete-confirmation-sub-title')}
        contentStyle={{ width: '30rem' }}
        danger
      />
    </div>
  );
};
